import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import {
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import { auth, db, storage } from "../firebaseConfig";
import { ref, deleteObject } from "firebase/storage";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { FaTrashAlt, FaUserCircle } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import Moment from "react-moment";
import useSnapshot from "../utils/useSnapshot";
import { toggleFavorite } from "../utils/fav";
import Pending from "../components/Pending";

const Event = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [ad, setAd] = useState(null);
  const [idx, setIdx] = useState(0);
  const [seller, setSeller] = useState(null);

  // Store average rating and total votes for seller
  const [sellerAvgRating, setSellerAvgRating] = useState(null);
  const [sellerRatingCount, setSellerRatingCount] = useState(0);

  const [showNumber, setShowNumber] = useState(false);

  // For favorites
  const { val } = useSnapshot("favorites", id);

  // Fetch ad data
  const getAd = async () => {
    const docRef = doc(db, "ads", id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setAd(docSnap.data());
      // Fetch seller data
      const postedById = docSnap.data().postedBy;
      const sellerRef = doc(db, "users", postedById);
      const sellerSnap = await getDoc(sellerRef);
      if (sellerSnap.exists()) {
        setSeller(sellerSnap.data());
        // Fetch seller rating
        await fetchSellerRating(postedById);
      }
    }
  };

  // Fetch the seller's rating
  const fetchSellerRating = async (userId) => {
    const ratingsRef = collection(db, "users", userId, "ratings");
    const snap = await getDocs(ratingsRef);

    if (!snap.empty) {
      let sum = 0;
      snap.forEach((doc) => {
        sum += doc.data().rating;
      });
      const avg = sum / snap.size;
      setSellerAvgRating(avg);
      setSellerRatingCount(snap.size);
    } else {
      setSellerAvgRating(null);
      setSellerRatingCount(0);
    }
  };

  useEffect(() => {
    getAd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Delete event
  const deleteAd = async () => {
    if (!ad) return;
    const confirmDel = window.confirm(`Delete ${ad.title}?`);
    if (confirmDel) {
      for (const image of ad.images) {
        const imgRef = ref(storage, image.path);
        await deleteObject(imgRef);
      }
      await deleteDoc(doc(db, "favorites", id));
      await deleteDoc(doc(db, "ads", id));
      navigate(`/profile/${auth.currentUser.uid}`);
    }
  };

  // Mark event as Pending
  const updateStatus = async () => {
    await updateDoc(doc(db, "ads", id), {
      isPending: true,
    });
    getAd(); // Reload ad
  };

  // Create chat room
  const createChatroom = async () => {
    if (!ad) return;
    const loggedInUser = auth.currentUser.uid;
    // unique chat ID
    const chatId =
      loggedInUser > ad.postedBy
        ? `${loggedInUser}.${ad.postedBy}.${id}`
        : `${ad.postedBy}.${loggedInUser}.${id}`;

    await setDoc(doc(db, "messages", chatId), {
      ad: id,
      users: [loggedInUser, ad.postedBy],
    });

    navigate("/chat", { state: { ad } });
  };

  if (!ad) return null;

  return (
    <div className="mt-5 container">
      <div className="row">
        {/* Carousel */}
        <div
          id="carouselExample"
          className="carousel slide col-md-8 position-relative"
        >
          {ad.isPending && <Pending singleAd={true} />}
          <div className="carousel-inner">
            {ad.images.map((image, i) => (
              <div
                className={`carousel-item ${idx === i ? "active" : ""}`}
                key={i}
              >
                <img
                  src={image.url}
                  alt={ad.title}
                  className="d-block w-100"
                  style={{
                    maxHeight: "500px",   // Ограничение по высоте
                    objectFit: "contain", // Показывать всю картинку целиком
                  }}
                />

                <button
                  className="carousel-control-prev"
                  type="button"
                  data-bs-target="#carouselExample"
                  data-bs-slide="prev"
                  onClick={() => setIdx(i)}
                >
                  <span
                    className="carousel-control-prev-icon"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Previous</span>
                </button>
                <button
                  className="carousel-control-next"
                  type="button"
                  data-bs-target="#carouselExample"
                  data-bs-slide="next"
                  onClick={() => setIdx(i)}
                >
                  <span
                    className="carousel-control-next-icon"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Next</span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Ad details */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              {/* Title + favorite icon */}
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="card-title">{ad.title}</h5>
                {auth.currentUser ? (
                  val?.users?.includes(auth.currentUser.uid) ? (
                    <AiFillHeart
                      size={30}
                      onClick={() => toggleFavorite(val.users, id)}
                      className="text-danger"
                      style={{ cursor: "pointer" }}
                    />
                  ) : (
                    <AiOutlineHeart
                      size={30}
                      onClick={() => toggleFavorite(val.users, id)}
                      className="text-danger"
                      style={{ cursor: "pointer" }}
                    />
                  )
                ) : null}
              </div>

              <div className="d-flex justify-content-between">
                <p className="card-text">
                  {ad.gender && (
                    <span>
                      For: {ad.gender}
                      <br />
                    </span>
                  )}
                  {ad.location && (
                    <span>
                      Location: {ad.location}
                      <br />
                    </span>
                  )}
                  {ad.eventDate && (
                    <span>
                      Event Date:{" "}
                      <Moment format="DD MMM YYYY">
                        {ad.eventDate.toDate()}
                      </Moment>
                      <br />
                    </span>
                  )}
                  Posted: <Moment fromNow>{ad.publishedAt.toDate()}</Moment>
                </p>
                {/* Delete button if user is the owner */}
                {ad.postedBy === auth.currentUser?.uid && (
                  <FaTrashAlt
                    size={25}
                    className="text-danger"
                    style={{ cursor: "pointer" }}
                    onClick={deleteAd}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Seller info */}
          <div className="card mt-3">
            <div className="card-body">
              <h5 className="card-title">Organizer</h5>
              <Link to={`/profile/${ad.postedBy}`}>
                <div className="d-flex align-items-center">
                  {seller?.photoUrl ? (
                    <img
                      src={seller.photoUrl}
                      alt={seller.name}
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        marginRight: "10px",
                      }}
                    />
                  ) : (
                    <FaUserCircle size={30} className="me-2" />
                  )}
                  <div>
                    <h6>{seller?.name}</h6>
                    {/* rating or 'No rating yet' */}
                    {sellerRatingCount > 0 ? (
                      <p style={{ marginBottom: 0 }}>
                        Rating: {sellerAvgRating?.toFixed(1)}/5 (
                        {sellerRatingCount}{" "}
                        {sellerRatingCount === 1 ? "vote" : "votes"})
                      </p>
                    ) : (
                      <p style={{ marginBottom: 0 }}>No rating yet</p>
                    )}
                  </div>
                </div>
              </Link>
            </div>

            <div>
              {auth.currentUser ? (
                <div className="text-center">
                  {showNumber ? (
                    <p>
                      <FiSend size={20} /> {ad.contact}
                    </p>
                  ) : (
                    <button
                      className="btn btn-secondary btn-sm mb-3"
                      onClick={() => setShowNumber(true)}
                    >
                      Show Contact Info
                    </button>
                  )}
                  <br />
                  {ad.postedBy !== auth.currentUser.uid && (
                    <button
                      className="btn btn-secondary btn-sm mb-3"
                      onClick={createChatroom}
                    >
                      Chat With Seller
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-center">
                  <Link
                    to="/auth/login"
                    state={{ from: location }}
                    className="text-primary"
                  >
                    Login
                  </Link>{" "}
                  to see contact info
                </p>
              )}
            </div>
          </div>

          {/* Pending button */}
          <div className="mt-5 text-center">
            {!ad.isPending && ad.postedBy === auth.currentUser?.uid && (
              <button className="btn btn-secondary btn-sm" onClick={updateStatus}>
                Mark as Pending
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Event description */}
      <div className="mt-5">
        <h3>{ad.title}</h3>
        <p>{ad.description}</p>
      </div>
    </div>
  );
};

export default Event;
